/**
 * Toucan Reads — everything that needs a PDF engine. It reads what a file
 * says about itself and draws its first page as a picture, as well as drawing
 * its pages for reading.
 *
 * The rendered first page is the only picture the app stores, because a
 * paper's title, authors and abstract tell it apart at card size. Every other
 * picture a card shows is a URL somewhere else.
 */

import { required } from "toucan";
import type { PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist";

/**
 * Longest edge of the stored first page: enough for a card on a dense screen,
 * and well under `MAX_PHOTO_EDGE`, which is sized for a photograph.
 */
export const PDF_THUMB_EDGE = 400;

/** The JPEG quality the stored first page is compressed to. */
export const PDF_THUMB_QUALITY = 0.75;

/** A page is cropped to this, so a grid of them is a grid rather than a row. */
export const PDF_THUMB_RATIO = 3 / 2;

/**
 * pdf.js, loaded the first time a PDF needs it, so a library with no PDFs
 * never parses it. The service worker precaches it with the rest of the app.
 *
 * Its parser runs in a Worker from `pdf.worker.js` in the app's directory
 * (see `pdf.worker.ts`), which keeps a long scan from freezing the screen.
 * pdf.js falls back to parsing on this thread if the script cannot be
 * fetched, so a device with an old cache still opens its papers.
 */
async function pdfjs() {
  const engine = await import("pdfjs-dist");
  engine.GlobalWorkerOptions.workerSrc = new URL(
    "pdf.worker.js",
    location.href,
  ).href;
  return engine;
}

/**
 * The options a file is opened with, which are the bytes and where the image
 * decoders are.
 *
 * A scanned page is usually a JBIG2 or JPEG 2000 image, and pdf.js decodes
 * both in WebAssembly it fetches at read time. Without an address for it
 * every scan draws blank but for its text layer. `assets.json` has the build
 * copy the decoders beside the bundle.
 */
function opening(bytes: Uint8Array<ArrayBuffer>) {
  return { data: bytes, wasmUrl: new URL("wasm/", location.href).href };
}

/**
 * The longest file whose text is read for a word count. A paper fits; a
 * scanned book does not, and reading every page for a number on a card is
 * not worth the stall.
 */
export const PDF_TEXT_PAGES = 50;

/** What a PDF says about itself, once it has been opened. */
export interface PdfContents {
  /** The title stated in the file's own metadata, where it states a real one. */
  title?: string;
  /** When the file says it was made, ISO-prefixed as every other date is. */
  publishedAt?: string;
  pages: number;
  /** The text layer, where the file was short enough to read whole. */
  text?: string;
  /** JPEG bytes of page one, at card size. */
  picture: Uint8Array<ArrayBuffer>;
}

/**
 * A PDF's own account of itself, with its metadata and length, its text and
 * a picture of page one. Undefined where the bytes were not a PDF this device
 * can read. One open for all of it, since the file can be tens of
 * megabytes to parse.
 */
export async function readPdf(
  bytes: Uint8Array<ArrayBuffer>,
): Promise<PdfContents | undefined> {
  const { getDocument } = await pdfjs();
  try {
    const file = await getDocument(opening(bytes)).promise;
    const first = await file.getPage(1);
    const contents: PdfContents = {
      pages: file.numPages,
      ...(await states(file)),
      picture: await thumbnail(first),
    };
    if (contents.title === undefined) {
      // Most papers state no title about themselves; every one of them prints
      // its title at the top of page one.
      const printed = titleFromPage(
        await pageRuns(first),
        first.getViewport({ scale: 1 }).height,
      );
      if (printed !== undefined) {
        contents.title = printed;
      }
    }
    const text = await textLayer(file);
    if (text !== undefined) {
      contents.text = text;
    }
    return contents;
  } catch (error) {
    // A file that is not a PDF, or one pdf.js will not open: the item stays a
    // PDF, and this console line is the only trace of a file the engine
    // refused.
    console.error(`Toucan Reads: could not read a PDF (${String(error)})`);
    return undefined;
  }
}

/** The fields a PDF's info dictionary states, as far as they are usable. */
async function states(
  file: PDFDocumentProxy,
): Promise<Pick<PdfContents, "title" | "publishedAt">> {
  const { info } = (await file.getMetadata()) as {
    info: { Title?: string; CreationDate?: string };
  };
  const stated: Pick<PdfContents, "title" | "publishedAt"> = {};
  const title = usableTitle(info.Title);
  if (title !== undefined) {
    stated.title = title;
  }
  const made = pdfDate(info.CreationDate);
  if (made !== undefined) {
    stated.publishedAt = made;
  }
  return stated;
}

/**
 * A stated title worth putting on a card. Producers fill the field in with the
 * source filename — `main.dvi`, `paper.tex`, `Microsoft Word - draft3.doc` —
 * often enough that a title which is plainly a filename is worse than the one
 * the address already gave.
 */
export function usableTitle(stated: string | undefined): string | undefined {
  const title = stated?.trim();
  if (title === undefined || title === "") {
    return undefined;
  }
  return /^(?:microsoft word - )|\.(?:dvi|tex|doc|docx|indd|qxd|pdf)$/i.test(
    title,
  )
    ? undefined
    : title;
}

/**
 * A page's text as the runs it was set in, with where each one sits. Exported
 * for `live:reads-pdf-titles`, which runs it over real papers.
 */
export async function pageRuns(page: PDFPageProxy): Promise<PageRun[]> {
  const content = await page.getTextContent();
  return content.items.flatMap((item) => {
    if (!("str" in item)) {
      return [];
    }
    // pdf.js types the placing matrix as `any[]`, but it is six numbers.
    const matrix = item.transform as number[];
    return [
      {
        text: item.str,
        size: item.height,
        x: required(matrix[4], "x"),
        y: required(matrix[5], "y"),
        // A rotated run has a skew in its matrix, and an upright one doesn't.
        upright: matrix[1] === 0,
      },
    ];
  });
}

/**
 * One run of text on a page, as far as choosing a title cares: what it says,
 * how tall its type is, and where on the page it sits.
 */
export interface PageRun {
  text: string;
  /** Height of the type, in the page's own units. */
  size: number;
  /** Distance up the page from its bottom edge, as PDF coordinates count it. */
  y: number;
  /** Distance in from the left edge. */
  x: number;
  /** False for text set sideways, which is a stamp down a margin. */
  upright: boolean;
}

/** Runs whose baselines are within this of each other share a line. */
const SAME_LINE = 2;

/** Type within this fraction of the largest counts as the same size. */
const SAME_SIZE = 0.95;

/** A title is in the top of the page, and a heading in the body is not one. */
const TITLE_BAND = 0.5;

/** Shorter than this is a page number or a running head, longer is a paragraph. */
const TITLE_LENGTH = { least: 8, most: 200 };

/**
 * The title a paper prints at the top of its own first page — the largest type
 * in the top half of it, read in order. Most papers state no usable title in
 * their metadata (arXiv's say `""`), but set their title larger than anything
 * else on page one.
 *
 * Text set sideways is skipped: arXiv's stamp down the left margin is set
 * large and would win.
 */
export function titleFromPage(
  runs: readonly PageRun[],
  pageHeight: number,
): string | undefined {
  const wanted = runs.filter(
    (run) =>
      run.upright && run.text.trim() !== "" && run.y >= pageHeight * TITLE_BAND,
  );
  const largest = Math.max(...wanted.map((run) => run.size), 0);
  const title = wanted
    .filter((run) => run.size >= largest * SAME_SIZE)
    // Down the page, then across it: a title over two lines reads in the order
    // it is set, and one line broken into runs by a font change reads across.
    .sort((a, b) => (Math.abs(a.y - b.y) < SAME_LINE ? a.x - b.x : b.y - a.y))
    .map((run) => run.text)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
  return title.length < TITLE_LENGTH.least || title.length > TITLE_LENGTH.most
    ? undefined
    : title;
}

/**
 * A `D:20170612195231+01'00'` turned into the ISO prefix everything else's
 * dates are kept as. Anything that is not that shape is no date at all — the
 * field is free text and plenty of producers put nothing usable in it.
 */
export function pdfDate(stated: string | undefined): string | undefined {
  const found = /^D:(\d{4})(\d{2})(\d{2})/.exec(stated ?? "");
  return found === null
    ? undefined
    : `${required(found[1], "year")}-${required(found[2], "month")}-${required(found[3], "day")}`;
}

/** Every page's text, run together — or undefined for a file past the cap. */
async function textLayer(file: PDFDocumentProxy): Promise<string | undefined> {
  if (file.numPages > PDF_TEXT_PAGES) {
    return undefined;
  }
  const pages: string[] = [];
  for (let number = 1; number <= file.numPages; number += 1) {
    const page = await file.getPage(number);
    const content = await page.getTextContent();
    pages.push(
      content.items
        .map((item) => ("str" in item ? item.str : ""))
        .join(" ")
        .replace(/\s+/g, " ")
        .trim(),
    );
  }
  return pages.join(" ").trim();
}

/**
 * Page one at card size, as JPEG bytes. This picture is stored and the PDF is
 * not, since a PDF is often near or past `MAX_BLOB_BYTES`.
 */
async function thumbnail(page: PDFPageProxy): Promise<Uint8Array<ArrayBuffer>> {
  const full = page.getViewport({ scale: 1 });
  const scale = PDF_THUMB_EDGE / Math.max(full.width, full.height);
  const canvas = await drawPage(page, scale, PDF_THUMB_RATIO);
  const blob = await new Promise((resolve: (blob: Blob | null) => void) => {
    canvas.toBlob(resolve, "image/jpeg", PDF_THUMB_QUALITY);
  });
  // toBlob only yields null on zero-sized canvases, which cannot happen here.
  return new Uint8Array(await required(blob, "blob").arrayBuffer());
}

/**
 * Draw a page onto a fresh canvas at `scale`. `crop` cuts the canvas down to
 * that aspect ratio, for the thumbnail but not for a page being read. A
 * page's top is its title and authors, which is the part worth a card, and
 * the whole page is the part worth reading.
 */
async function drawPage(
  page: PDFPageProxy,
  scale: number,
  crop?: number,
): Promise<HTMLCanvasElement> {
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(viewport.width);
  canvas.height =
    crop === undefined
      ? Math.round(viewport.height)
      : Math.min(Math.round(viewport.height), Math.round(canvas.width / crop));
  const context = required(canvas.getContext("2d"), "canvas context");
  await page.render({ canvas, canvasContext: context, viewport }).promise;
  return canvas;
}

/**
 * A PDF ready to read. It gives the shape of every page in it, and the pages
 * themselves drawn at `width` CSS pixels, one at a time as they finish.
 *
 * Every shape comes first, as width over height, so the reader can reserve
 * each page's room at any pane width. A pane that grew a page at a time would
 * move the paragraph being read, and put a saved place past the bottom of
 * what is drawn. A shape is cheap: it comes from the page's dictionary, with
 * nothing rendered.
 *
 * The pages come from a generator, so page one is readable while later ones
 * are still drawing, and a reader who leaves stops the work by leaving the
 * loop.
 */
export async function pdfSheets(
  bytes: Uint8Array<ArrayBuffer>,
  width: number,
): Promise<{
  shapes: number[];
  pages: AsyncGenerator<HTMLCanvasElement>;
}> {
  const { getDocument } = await pdfjs();
  const file = await getDocument(opening(bytes)).promise;
  const numbers = Array.from({ length: file.numPages }, (_, at) => at + 1);
  const viewports = await Promise.all(
    numbers.map(async (number) =>
      (await file.getPage(number)).getViewport({ scale: 1 }),
    ),
  );
  async function* pages(): AsyncGenerator<HTMLCanvasElement> {
    for (const number of numbers) {
      const page = await file.getPage(number);
      const full = page.getViewport({ scale: 1 });
      yield await drawPage(page, (width * devicePixelRatio) / full.width);
    }
  }
  return {
    shapes: viewports.map((viewport) => viewport.width / viewport.height),
    pages: pages(),
  };
}
